import React from 'react';
import {
  Box, Grid, Button, Divider, CircularProgress, Typography,
  IconButton, TextField, Paper,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { VisibilityOutlined } from '@mui/icons-material';
import { postRequest } from 'Includes/api/apicall';
import { POST_URL } from 'Includes/urls';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
function ServiceAdd(props) {
  const [loading, set_loading] = React.useState(false);
  const [submitDisable, set_submitDisable] = React.useState(false);
  const navigate = useNavigate();
  const [services, setServices] = React.useState([{ id: 0 }]);
  const [serviceDetails, setServiceDetails] = React.useState({
    0: {
      service_name: '',
      service_code: '',
      service_description: '',
    }
  });
  const [serviceErrors, setServiceErrors] = React.useState({});

  const addService = () => {
    const newId = services.length > 0 ? Math.max(...services.map(s => s.id)) + 1 : 0;
    setServices([...services, { id: newId }]);
    setServiceDetails(prev => ({
      ...prev,
      [newId]: {
        service_name: '',
        service_code: '',
        service_description: '',
      }
    }));
  };

  const removeService = (serviceId) => {
    if (services.length > 1) {
      setServices(services.filter(s => s.id !== serviceId));
      const newServiceDetails = { ...serviceDetails };
      delete newServiceDetails[serviceId];
      setServiceDetails(newServiceDetails);

      const newErrors = { ...serviceErrors };
      Object.keys(newErrors).forEach(key => {
        if (key.startsWith(`${serviceId}_`)) {
          delete newErrors[key];
        }
      });
      setServiceErrors(newErrors);
    }
  };

  const handleFieldChange = (serviceId, fieldName, value) => {
    setServiceDetails(prev => ({
      ...prev,
      [serviceId]: {
        ...prev[serviceId],
        [fieldName]: value
      }
    }));

    setServiceErrors(prev => ({
      ...prev,
      [`${serviceId}_${fieldName}`]: ''
    }));
  };

  const validate = () => {
    const errors = {};
    let isValid = true;

    services.forEach((service) => {
      const data = serviceDetails[service.id];

      if (!data.service_name || data.service_name.trim() === '') {
        errors[`${service.id}_service_name`] = 'Service Name is required';
        isValid = false;
      }

      if (!data.service_code || data.service_code.trim() === '') {
        errors[`${service.id}_service_code`] = 'Service Code is required';
        isValid = false;
      }
    });

    setServiceErrors(errors);
    return isValid;
  };

  const submit = () => {
    if (validate()) {
      set_submitDisable(true);

      const servicesData = services.map((service) => ({
        name: serviceDetails[service.id].service_name,
        code: serviceDetails[service.id].service_code,
        service_description: serviceDetails[service.id].service_description,
      }));

      const url = POST_URL.services?.api;
      postRequest(url, servicesData, {}).then(response => {
        set_submitDisable(false);
        if (response && response.status === 200) {
          Swal.fire({
            position: 'top-end',
            type: 'success',
            title: response.data.Reason || 'Success',
            text: 'Service(s) added successfully',
            showConfirmButton: false,
            timer: 1500
          });
          navigate('/billing/service/list');
        }
      }).catch(() => {
        set_submitDisable(false);
      });
    }
  };

  const resetForm = () => {
    setServices([{ id: 0 }]);
    setServiceDetails({
      0: {
        service_name: '',
        service_code: '',
        service_description: '',
      }
    });
    setServiceErrors({});
  };

  return (
    <>
      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      )}
      {!loading && (
        <Box
          sx={{
            p: { xs: 2, sm: 3 },
            width: '100%',
            bgcolor: 'background.default',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              justifyContent: 'space-between',
              alignItems: { xs: 'stretch', sm: 'center' },
              mb: 2,
            }}
          >
            <Box>
              <Typography variant="h5" fontWeight={700} sx={{ color: '#0a0a0a' }}>
                Add services
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
                Create catalog services with name, code, and description.
              </Typography>
            </Box>

            <Box display="flex" gap={1} flexWrap="wrap" alignSelf={{ xs: 'flex-start', sm: 'center' }}>
              <Button
                variant="contained"
                startIcon={<VisibilityOutlined />}
                onClick={() => {
                  if (navigate.length > 1) {
                    navigate(-1);
                  } else {
                    navigate('/billing/service/list');
                  }
                }}
                sx={{ textTransform: 'none', borderRadius: 2, px: 2.5, py: 1 }}
              >
                View services
              </Button>
            </Box>
          </Box>

          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3 },
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            {services.map((service, index) => (
              <Paper
                key={service.id}
                elevation={0}
                variant="outlined"
                sx={{
                  mb: 3,
                  p: 2,
                  borderRadius: 2,
                  borderColor: 'divider',
                  bgcolor: 'grey.50',
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle2" sx={{ color: '#0a0a0a', fontWeight: 700 }}>
                    Service {index + 1}
                  </Typography>
                  {services.length > 1 && (
                    <IconButton
                      size="small"
                      color="inherit"
                      onClick={() => removeService(service.id)}
                      title="Remove this service"
                    >
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="Service Name"
                      variant="outlined"
                      fullWidth
                      required
                      size="small"
                      value={serviceDetails[service.id]?.service_name || ''}
                      onChange={(e) => handleFieldChange(service.id, 'service_name', e.target.value)}
                      error={!!serviceErrors[`${service.id}_service_name`]}
                      helperText={serviceErrors[`${service.id}_service_name`] || ''}
                      inputProps={{ maxLength: 100 }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="Service Code"
                      variant="outlined"
                      fullWidth
                      required
                      size="small"
                      value={serviceDetails[service.id]?.service_code || ''}
                      onChange={(e) => handleFieldChange(service.id, 'service_code', e.target.value)}
                      error={!!serviceErrors[`${service.id}_service_code`]}
                      helperText={serviceErrors[`${service.id}_service_code`] || ''}
                      inputProps={{ maxLength: 50 }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label="Service Description"
                      variant="outlined"
                      fullWidth
                      size="small"
                      value={serviceDetails[service.id]?.service_description || ''}
                      onChange={(e) => handleFieldChange(service.id, 'service_description', e.target.value)}
                      inputProps={{ maxLength: 250 }}
                      multiline
                      minRows={2}
                    />
                  </Grid>
                </Grid>
              </Paper>
            ))}

            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={addService}
              sx={{ textTransform: 'none', mb: 2 }}
            >
              Add another service
            </Button>

            <Divider sx={{ my: 2 }} />

            <Box display="flex" justifyContent="flex-end" gap={2} flexWrap="wrap">
              <Button variant="outlined" onClick={resetForm} sx={{ textTransform: 'none', borderRadius: 2, px: 2 }}>
                Reset
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={submitDisable}
                onClick={submit}
                sx={{ textTransform: 'none', borderRadius: 2, px: 3 }}
              >
                Submit
              </Button>
            </Box>
          </Paper>
        </Box>
      )}
    </>
  );
}

export default ServiceAdd;
